import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-bs5';
import { WkoLoadingSpinner } from '../WkoLoadingSpinner';
import { NcAcknowledgementModal } from './NcAcknowledgementModal';
import { NcReviewMainContent, } from './NcReviewMainContent';
import { notifyError, notifySuccess } from '../../utils/notifications';
DataTable.use(DT);
const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const parseRequiredInt = (searchParams, key) => {
    const value = searchParams.get(key);
    const parsedValue = value === null ? Number.NaN : Number.parseInt(value, 10);
    if (Number.isNaN(parsedValue)) {
        throw new Error(`Missing or invalid '${key}' query parameter.`);
    }
    return parsedValue;
};
const parseQueryData = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const selectedLevels = (searchParams.get('selectedLevels') ?? '')
        .split(',')
        .map((value) => Number.parseInt(value, 10))
        .filter((value) => !Number.isNaN(value));
    return {
        selectedMonthFrom: parseRequiredInt(searchParams, 'selectedMonthFrom'),
        selectedMonthTo: parseRequiredInt(searchParams, 'selectedMonthTo'),
        selectedYearFrom: parseRequiredInt(searchParams, 'selectedYearFrom'),
        selectedYearTo: parseRequiredInt(searchParams, 'selectedYearTo'),
        selectedFleetId: parseRequiredInt(searchParams, 'selectedFleetId'),
        selectedVesselId: parseRequiredInt(searchParams, 'selectedVesselId'),
        selectedLevels,
        showAcknowledged: searchParams.get('showAcknowledged') === 'true',
    };
};
const getNcKey = (nc) => {
    return [nc.VesselId, nc.CrewId, nc.NcDate, nc.NcDetails, nc.NcLevel].join('|');
};
const areStringArraysEqual = (left, right) => {
    if (left.length !== right.length) {
        return false;
    }
    return left.every((value, index) => value === right[index]);
};
export const NcReviewPage = ({ reportBaseUrl }) => {
    const tableRef = useRef(null);
    const [queryData, setQueryData] = useState(null);
    const [queryError, setQueryError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isUsingTwoWaySync, setIsUsingTwoWaySync] = useState(false);
    const [vesselEmail, setVesselEmail] = useState('');
    const [nonConformities, setNonConformities] = useState([]);
    const [showAcknowledged, setShowAcknowledged] = useState(false);
    const [comments, setComments] = useState('');
    const [selectedNcKeys, setSelectedNcKeys] = useState([]);
    const [currentPageSelectableKeys, setCurrentPageSelectableKeys] = useState([]);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    useEffect(() => {
        try {
            const parsedQueryData = parseQueryData();
            setQueryData(parsedQueryData);
            setShowAcknowledged(parsedQueryData.showAcknowledged);
        }
        catch (error) {
            const message = error instanceof Error ? error.message : 'Invalid query parameters.';
            setQueryError(message);
            notifyError(message);
        }
    }, []);
    useEffect(() => {
        if (!queryData) {
            return;
        }
        const params = new URLSearchParams({ vesselId: String(queryData.selectedVesselId) });
        fetch(`/api/ncreview/isusingtwowaysync?${params}`)
            .then(async (response) => {
            if (!response.ok) {
                throw new Error(await response.text() || 'Could not load vessel sync configuration.');
            }
            return response.json();
        })
            .then((result) => setIsUsingTwoWaySync(result))
            .catch((error) => notifyError(error.message));
    }, [queryData]);
    useEffect(() => {
        if (!queryData) {
            return;
        }
        const loadData = async () => {
            setLoading(true);
            setSelectedNcKeys([]);
            const vesselParams = new URLSearchParams({ vesselId: String(queryData.selectedVesselId) });
            const listParams = new URLSearchParams({
                monthFrom: String(queryData.selectedMonthFrom + 1),
                yearFrom: String(queryData.selectedYearFrom),
                monthTo: String(queryData.selectedMonthTo + 1),
                yearTo: String(queryData.selectedYearTo),
                vesselId: String(queryData.selectedVesselId),
                levels: queryData.selectedLevels.join(','),
            });
            try {
                const [emailResponse, ncResponse] = await Promise.all([
                    fetch(`/api/ncreview/vesselemail?${vesselParams}`),
                    fetch(`/api/ncreview/?${listParams}`),
                ]);
                if (!emailResponse.ok) {
                    throw new Error(await emailResponse.text() || 'Could not load vessel email.');
                }
                if (ncResponse.status === 400) {
                    throw new Error(await ncResponse.text());
                }
                if (!ncResponse.ok) {
                    throw new Error('An error has occured');
                }
                const [email, ncs] = await Promise.all([
                    emailResponse.text(),
                    ncResponse.json(),
                ]);
                setVesselEmail(email);
                setNonConformities(ncs);
            }
            catch (error) {
                notifyError(error instanceof Error ? error.message : 'An error has occured');
            }
            finally {
                setLoading(false);
            }
        };
        void loadData();
    }, [queryData]);
    const displayedNonConformities = useMemo(() => {
        const filteredRows = showAcknowledged
            ? nonConformities
            : nonConformities.filter((nc) => !nc.AlreadyAcknowledged);
        const sortedRows = [...filteredRows];
        sortedRows.sort((left, right) => {
            const crewComparison = (left.Crew ?? '').localeCompare(right.Crew ?? '');
            if (crewComparison !== 0) {
                return crewComparison;
            }
            return new Date(right.NcDate).getTime() - new Date(left.NcDate).getTime();
        });
        return sortedRows;
    }, [nonConformities, showAcknowledged]);
    const displayedRows = useMemo(() => displayedNonConformities.map((nc) => ({
        ...nc,
        IsSelected: selectedNcKeys.includes(getNcKey(nc)),
    })), [displayedNonConformities, selectedNcKeys]);
    const selectedNcs = useMemo(() => nonConformities.filter((nc) => selectedNcKeys.includes(getNcKey(nc))), [nonConformities, selectedNcKeys]);
    const syncCurrentPageSelectableKeys = () => {
        const dataTable = tableRef.current?.dt();
        const pageRows = dataTable?.rows({ page: 'current' }).data().toArray();
        const nextKeys = (pageRows ?? displayedRows.slice(0, 10))
            .filter((nc) => !nc.AlreadyAcknowledged)
            .map((nc) => getNcKey(nc));
        setCurrentPageSelectableKeys((currentKeys) => (areStringArraysEqual(currentKeys, nextKeys) ? currentKeys : nextKeys));
    };
    useEffect(() => {
        syncCurrentPageSelectableKeys();
    }, [displayedRows]);
    const selectedAll = currentPageSelectableKeys.length > 0
        && currentPageSelectableKeys.every((key) => selectedNcKeys.includes(key));
    const vesselName = nonConformities[0]?.Vessel ?? '';
    const listDetails = useMemo(() => {
        if (!queryData) {
            return '';
        }
        const timePeriod = `${monthNames[queryData.selectedMonthFrom]} ${queryData.selectedYearFrom} - ${monthNames[queryData.selectedMonthTo]} ${queryData.selectedYearTo}`;
        const levels = queryData.selectedLevels.join(', ');
        return `${timePeriod} (Selected NC level${queryData.selectedLevels.length > 1 ? 's' : ''}: ${levels})`;
    }, [queryData]);
    const sendViaText = isUsingTwoWaySync
        ? '(acknowledgements will appear on Watchkeeper on board)'
        : `(review will be emailed to: ${vesselEmail})`;
    const toggleNc = (nc) => {
        const key = getNcKey(nc);
        setSelectedNcKeys((currentKeys) => (currentKeys.includes(key)
            ? currentKeys.filter((currentKey) => currentKey !== key)
            : [...currentKeys, key]));
    };
    const toggleAll = () => {
        if (selectedAll) {
            setSelectedNcKeys((currentKeys) => currentKeys.filter((key) => !currentPageSelectableKeys.includes(key)));
            return;
        }
        setSelectedNcKeys((currentKeys) => Array.from(new Set([...currentKeys, ...currentPageSelectableKeys])));
    };
    const acknowledgeSelection = async () => {
        if (!queryData) {
            return;
        }
        if (selectedNcs.length === 0) {
            notifyError('Please select NCs to acknowledge');
            return;
        }
        setIsConfirmOpen(false);
        let response;
        try {
            response = await fetch(`/api/ncreview/?${new URLSearchParams({ vesselId: String(queryData.selectedVesselId) })}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ NCs: selectedNcs, Comment: comments }),
            });
        }
        catch {
            notifyError('An error has occured');
            return;
        }
        if (!response.ok) {
            notifyError('An error has occured');
            return;
        }
        notifySuccess('Non-conformities acknowledged');
        setComments('');
        setLoading(true);
        const listParams = new URLSearchParams({
            monthFrom: String(queryData.selectedMonthFrom + 1),
            yearFrom: String(queryData.selectedYearFrom),
            monthTo: String(queryData.selectedMonthTo + 1),
            yearTo: String(queryData.selectedYearTo),
            vesselId: String(queryData.selectedVesselId),
            levels: queryData.selectedLevels.join(','),
        });
        try {
            const refreshedResponse = await fetch(`/api/ncreview/?${listParams}`);
            if (refreshedResponse.status === 400) {
                throw new Error(await refreshedResponse.text());
            }
            if (!refreshedResponse.ok) {
                throw new Error('An error has occured');
            }
            const refreshedNcs = await refreshedResponse.json();
            setNonConformities(refreshedNcs);
            setSelectedNcKeys([]);
        }
        catch (error) {
            notifyError(error instanceof Error ? error.message : 'An error has occured');
        }
        finally {
            setLoading(false);
        }
    };
    if (queryError) {
        return (_jsx("div", { className: "container", children: _jsx("div", { className: "alert alert-danger", role: "alert", children: queryError }) }));
    }
    if (!queryData) {
        return (_jsx("div", { className: "container py-4", children: _jsx(WkoLoadingSpinner, {}) }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(NcReviewMainContent, { summary: {
                    vesselName,
                    listDetails,
                    sendViaText,
                }, filters: {
                    showAcknowledged,
                    onShowAcknowledgedChange: setShowAcknowledged,
                }, actions: {
                    onOpenConfirm: () => setIsConfirmOpen(true),
                }, loading: loading, reportBaseUrl: reportBaseUrl, table: {
                    tableRef,
                    displayedRows,
                    selectedAll,
                    onToggleAll: toggleAll,
                    onToggleNc: toggleNc,
                    currentPageSelectableCount: currentPageSelectableKeys.length,
                    onDraw: syncCurrentPageSelectableKeys,
                } }), isConfirmOpen ? (_jsx(NcAcknowledgementModal, { comments: comments, onCommentsChange: setComments, onClose: () => setIsConfirmOpen(false), onConfirm: () => void acknowledgeSelection() })) : null] }));
};
